import {
  type CreateSubmissionBody,
  type SubmissionCreateWireOutcome,
  completeSubmission as completeSubmissionRequest,
  createSubmission as createSubmissionRequest,
  failSubmission as failSubmissionRequest,
  fetchLocalSubmissionsStatus as fetchLocalSubmissionsStatusRequest,
  getSubmission as getSubmissionRequest,
  getUploadOffset as getUploadOffsetRequest,
  reportProgress as reportProgressRequest,
  restartExpiredSubmission as restartExpiredSubmissionRequest,
  resumableBegin as resumableBeginRequest,
} from '@/features/uploads/api/submissions';
import { toSubmission } from '@/features/uploads/lib/transferModels';
import type {
  LocalSubmissionStatusItem,
  RestartUploadResponse,
  Submission,
} from '@/features/uploads/types/uploads';
import type { ApiCallOptions } from '@/shared/api/client';

export type SubmissionCreateOutcome =
  | { kind: 'created'; submission: Submission }
  | Exclude<SubmissionCreateWireOutcome, { kind: 'created' }>;

export async function createSubmission(
  body: CreateSubmissionBody,
): Promise<SubmissionCreateOutcome> {
  const outcome = await createSubmissionRequest(body);
  return outcome.kind === 'created'
    ? { ...outcome, submission: toSubmission(outcome.submission) }
    : outcome;
}

export async function resumableBegin(sessionId: string, contentType?: string): Promise<Submission> {
  return toSubmission(await resumableBeginRequest(sessionId, contentType));
}

export async function reportProgress(
  sessionId: string,
  partNo: number,
  cumulativeBytes: number,
): Promise<Submission> {
  return toSubmission(await reportProgressRequest(sessionId, partNo, cumulativeBytes));
}

export async function completeSubmission(sessionId: string): Promise<Submission> {
  return toSubmission(await completeSubmissionRequest(sessionId));
}

export async function failSubmission(sessionId: string, reason: string): Promise<Submission> {
  return toSubmission(await failSubmissionRequest(sessionId, reason));
}

export async function getSubmission(sessionId: string): Promise<Submission> {
  return toSubmission(await getSubmissionRequest(sessionId));
}

export async function getUploadOffset(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<number> {
  const response = await getUploadOffsetRequest(sessionId, options);
  return Number(response.offset);
}

export async function restartExpiredSubmission(
  sessionId: string,
  replacementSessionId: string,
  options: ApiCallOptions = {},
): Promise<RestartUploadResponse> {
  const response = await restartExpiredSubmissionRequest(sessionId, replacementSessionId, options);
  return {
    action: response.action,
    previousSession: toSubmission(response.previousSession),
    session: toSubmission(response.session),
  };
}

export async function fetchLocalSubmissionsStatus(
  sessionIds: string[],
  options: ApiCallOptions = {},
): Promise<LocalSubmissionStatusItem[]> {
  return (await fetchLocalSubmissionsStatusRequest(sessionIds, options)).items;
}
